import { CounterGroups, LoadingIndicationService } from 'src/app/services/loading-indication.service';
import { HttpClient, HttpContext, HttpContextToken, HttpErrorResponse, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { defer, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IMappingObject } from '../models/imapping-object';
import { BaseServiceConfig, BASE_SERVICE_CONFIG_INJECTION_TOKEN } from './base.service.config';
import { LocalStorageService } from './local-storage.service';
import { LoginResult } from '../models/login-result';
import { NotificationService } from './notification.service';
import { LoadService } from './load.service';
import { DOCUMENT } from '@angular/common';
import { NavigationService } from '../core/navigation/services/navigation.service';

export const NO_BLOCK: HttpContextToken<boolean> = new HttpContextToken<boolean>(() => { return false; });
export const REQUIRES_AUTHENTICATION: HttpContextToken<boolean> = new HttpContextToken<boolean>(() => { return true; });

@Injectable({
	providedIn: 'root'
})

export class BaseService {
	private defaultHttpOptions = { withCredentials: true };

	//added for Pentest result
	private optionsSecurity = {
		'Strict-Transport-Security': 'max-age=31536000; includeSubDomains', 'X-Content-Type-Options': 'nosniff'
		, 'Content-Security-Policy': "default-src 'self'; object-src 'none'; child-src 'self'; frame-ancestors 'none'; upgrade-insecure-requests; block-all-mixed-content"
	}
	constructor(
		@Inject(BASE_SERVICE_CONFIG_INJECTION_TOKEN) private config: BaseServiceConfig,
		private http: HttpClient,
		private router: Router,
		private localStorageService: LocalStorageService,
		private _notificationService: NotificationService,
		private loadService: LoadService,
		private _loadingIndicationService: LoadingIndicationService,

		private _navigationService: NavigationService,

		@Inject(DOCUMENT) private document: Document
	) { }

	public authHeaders: HttpHeaders = new HttpHeaders();

	public getOuter<T>(url: string, requestOptions?: RequestOptions, mappingType?: new () => IMappingObject): Observable<T> {
		const request = this.http.get<T>(url, {

		});
		return this.prepareRequest(url, request, requestOptions, mappingType);
	}

	public get<T>(url: string, requestOptions?: RequestOptions, mappingType?: new () => IMappingObject, silent = false, responseType: 'json' | 'text' = 'json'): Observable<T> {

		url = this.getCorrectUrl(url);

		const options = this.getOptions(silent);

		if (responseType !== 'json') {
			// Type assertion to satisfy TypeScript.
			(options as any).responseType = responseType;
		}

		const request = this.http.get<T>(url, options);

		return this.prepareRequest(url, request, requestOptions, mappingType);
	}

	public getBlob<T>(url: string, requestOptions?: RequestOptions, mappingType?: new () => IMappingObject, silent = false): Observable<T> {

		url = this.getCorrectUrl(url);

		const options = this.getOptions(silent);
		const headers = { ...options.headers };
		delete headers['Content-Type'];

		const request = this.http.get<T>(url, { ...options, headers, responseType: 'blob' as 'json' });

		return this.prepareRequest(url, request, requestOptions, mappingType);
	}

	public put<T>(url: string, body: any, requestOptions?: RequestOptions, mappingType?: new () => IMappingObject, silent = false): Observable<T> {
		url = this.getCorrectUrl(url);

		const request = this.http.put<T>(url, body, this.getOptions(silent));

		return this.prepareRequest(url, request, requestOptions, mappingType);
	}

	public post<T>(url: string, body?: any, requestOptions?: RequestOptions, mappingType?: new () => IMappingObject, silent = false): Observable<T> {
		url = this.getCorrectUrl(url);

		const request = this.http.post<T>(url, body, this.getOptions(silent));

		return this.prepareRequest(url, request, requestOptions, mappingType);
	}

	public postFormData<T>(url: string, body?: FormData, requestOptions?: RequestOptions, mappingType?: new () => IMappingObject, silent = false): Observable<T> {
		url = this.getCorrectUrl(url);

		const options = this.getOptions(silent);
		const headers = { ...options.headers };
		delete headers['Content-Type'];

		const request = this.http.post<T>(url, body, { ...options, headers });

		return this.prepareRequest(url, request, requestOptions, mappingType);
	}

	public delete<T>(url: string, requestOptions?: RequestOptions, mappingType?: new () => IMappingObject, silent = false): Observable<T> {
		url = this.getCorrectUrl(url);

		const request = this.http.delete<T>(url, this.getOptions(silent));

		return this.prepareRequest(url, request, requestOptions, mappingType);
	}

	public postArrayBuffer(url: string, body: ArrayBuffer, headers: { name: string; value: string }[], requestOptions?: RequestOptions): Observable<any> {
		let headersToSet = new HttpHeaders({
			name: 'Content-Type',
			value: 'application/octet-stream'
		});
		headers.forEach(h => {
			headersToSet = headersToSet.append(h.name, h.value);
		});

		url = this.getCorrectUrl(url);
		const request = new HttpRequest(
			'POST',
			url,
			body,
			Object.assign(
				{
					headers: headersToSet,
					reportProgress: true
				},
				this.defaultHttpOptions
			)
		);

		return this.prepareRequest(url, this.http.request(request), requestOptions);
	}

	public checkSession(): boolean {
		let authorizationValue = this.localStorageService.get<LoginResult>(LoginResult.storage_key);
		if (this.document != null && this.document.location != null &&
			this.document.location.href != null && this.document.location.href.includes("forgot-password")) {
			return false;
		}
		else if (!authorizationValue || new Date(authorizationValue.expireDate) < new Date()) {

			return false;
		}
		return true;
	}
	private getOptions(silent = false): any {
		let authorizationValue = this.localStorageService.get<LoginResult>(LoginResult.storage_key);
		if (this.document != null && this.document.location != null &&
			this.document.location.href != null && this.document.location.href.includes("forgot-password")) {

		}
		else if (!authorizationValue || new Date(authorizationValue.expireDate) < new Date()) {

			return { headers: { Pragma: 'no-cache', 'Content-Type': 'application/json', ...this.optionsSecurity } };
		}

		let authorizationHeader = authorizationValue ? "Bearer " + authorizationValue.token : '';

		const options = silent
			? { headers: { silent: silent.toString(), Expires: '0', Pragma: 'no-cache', authorization: authorizationHeader, 'Content-Type': 'application/json', ...this.optionsSecurity } }
			: { headers: { Expires: '0', Pragma: 'no-cache', authorization: authorizationHeader, 'Content-Type': 'application/json', ...this.optionsSecurity } };

		return Object.assign(options, this.defaultHttpOptions, this.optionsSecurity);
	}

	public getNewOptions(silent = false): any {
		let authorizationValue = this.localStorageService.get<LoginResult>(LoginResult.storage_key);
		if (this.document != null && this.document.location != null &&
			this.document.location.href != null && this.document.location.href.includes("forgot-password")) {
		}
		else if (!authorizationValue || new Date(authorizationValue.expireDate) < new Date()) {

			return { headers: { Pragma: 'no-cache', 'Content-Type': 'application/json', ...this.optionsSecurity } };
		}

		let authorizationHeader = authorizationValue ? "Bearer " + authorizationValue.token : '';
		const options = silent
			? { headers: { silent: silent.toString(), Expires: '0', Pragma: 'no-cache', authorization: authorizationHeader, 'Content-Type': 'application/json', ...this.optionsSecurity } }
			: { headers: { Expires: '0', Pragma: 'no-cache', authorization: authorizationHeader, 'Content-Type': 'application/json', ...this.optionsSecurity } };
		return Object.assign(options, this.defaultHttpOptions, this.optionsSecurity);
	}

	public getOptionsWithoptions(silent = false): any {
		const options = this.getNewOptions(silent);
		return options;
	}

	private prepareRequest<T>(url: string, observable: Observable<T>, requestOptions: RequestOptions, mapperType?: new () => IMappingObject): Observable<any> {

		//? We use defer because then when we don't call subscribe on a request - we won't get loading indicator until we actually make a call to subscribe().
		return defer(
			() => {
				this.loadService.startRequest();

				requestOptions = Object.assign({ ...RequestOptions.DEFAULT }, requestOptions);

				let loadingTimeout: NodeJS.Timeout;

				if (requestOptions.showLoadingIndicator) {
					loadingTimeout = setTimeout(
						() => {
							if (loadingTimeout) {
								clearTimeout(loadingTimeout);

								loadingTimeout = null;
							}

							this._loadingIndicationService.register(
								{
									groupType: requestOptions.loadingIndicatorCounterGroupType,
									groupId: requestOptions.loadingIndicatorCounterGroupId
								},
								requestOptions.name ?? url,
								requestOptions.loadingIndicatorLoggingEnabled
							);
						},
						requestOptions.loadingIndicatorTimeThreshold
					);
				}

				return observable.pipe(
					map(
						(response: T) => {
							this.loadService.stopRequest();

							if (requestOptions.showLoadingIndicator) {
								if (loadingTimeout) {
									clearTimeout(loadingTimeout);

									loadingTimeout = null;
								}

								this._loadingIndicationService.unregister(
									{
										groupType: requestOptions.loadingIndicatorCounterGroupType,
										groupId: requestOptions.loadingIndicatorCounterGroupId
									},
									requestOptions.name ?? url,
									requestOptions.loadingIndicatorLoggingEnabled
								);
							}

							if (requestOptions.responseLoggingEnabled) {
								console.log(`${requestOptions.name} - response:`, response);
							}

							if (mapperType) {
								const mapper = new mapperType();
								if (response instanceof Array) {
									return response.map(item => mapper.map(item));
								} else {
									return mapper.map(response);
								}
							}

							return response;
						}
					),
					catchError(
						(errorResponse: HttpErrorResponse) => {
							this.loadService.stopRequest();

							if (requestOptions.showLoadingIndicator) {
								if (loadingTimeout) {
									clearTimeout(loadingTimeout);

									loadingTimeout = null;
								}

								this._loadingIndicationService.unregister(
									{
										groupType: requestOptions.loadingIndicatorCounterGroupType,
										groupId: requestOptions.loadingIndicatorCounterGroupId
									},
									requestOptions.name ?? url,
									requestOptions.loadingIndicatorLoggingEnabled
								);
							}

							if (!this.handleError(errorResponse, requestOptions) && requestOptions.showErrorNotification) {
								//this._notificationService.error(`Error: ${errorResponse.error} | Message: ${errorResponse.message}`);
								console.error(`Error: ${errorResponse.error} | Message: ${errorResponse.message}`);
							}

							if (requestOptions.errorLoggingEnabled) {
								console.error(`Error: ${errorResponse.error}`, `Message: ${errorResponse.message}`);
							}

							//! This has to throw an error in any situation (even when `handleError` was successful) because any other subscriptions of errors to Observable won't work otherwise.
							return throwError(() => errorResponse);
						}
					)
				);
			}
		)
	}
	/** returns true if handled */
	private handleError(response: HttpErrorResponse, requestOptions: RequestOptions): boolean {
		if (requestOptions.errorLoggingEnabled) {
			switch (response.status) {
				/** no connection */
				case 0:
					this._notificationService.error("Can't connect to api server.");
					return true;

				case 400:
					console.error("Client error, server cannot or will not process the request. The client should not repeat this request without modification. (400)");

					//this._notificationService.error("Client error, server cannot or will not process the request. The client should not repeat this request without modification. (400)");

					return true;

				/** not authenticated */
				case 401:
					if (this.document != null && this.document.location != null &&
						this.document.location.href != null && this.document.location.href.includes("forgot-password")) {
					}
					else {

					}

					return true;

				/** not authorized */
				case 403:
					this._notificationService.error("You have no permissions to do that (403).");

					return true;

				default:
					if (response.status >= 500) {
						this._notificationService.error(`Internal server error (${response.status}): ${response.error}`);

						return true;
					}

					break;
			}
		}
		return false;
	}

	public getCorrectUrl(url: string): string {

		if (!url.startsWith('api') && !url.startsWith('/api')) {
			url = '/api' + (url.startsWith('/') ? '' : '/') + url;
		}

		const fullUrl = this.config.apiEndpoint + (url.startsWith('/') ? '' : '/') + url;

		return encodeURI(fullUrl);
	}


}

export class RequestOptions {
	public name?: string;
	public loadingIndicatorCounterGroupType?: CounterGroups = CounterGroups.PAGE_LOAD;
	public loadingIndicatorCounterGroupId?: string;
	public loadingIndicatorTimeThreshold?: number;
	public showLoadingIndicator?: boolean = true;
	public showErrorNotification?: boolean = true;

	public loadingIndicatorLoggingEnabled?: boolean = false;
	public responseLoggingEnabled?: boolean = false;
	public errorLoggingEnabled?: boolean = true;
	public handleErrorEnabled?: boolean = true;
	public requiresAuthentication?: boolean = true;
	public context?: HttpContext = new HttpContext();
	public withCredentials?: boolean = false;

	public static readonly DEFAULT: RequestOptions = {
		loadingIndicatorCounterGroupType: CounterGroups.PAGE_LOAD,
		loadingIndicatorTimeThreshold: 100,
		showLoadingIndicator: true,
		showErrorNotification: true,

		loadingIndicatorLoggingEnabled: false,
		responseLoggingEnabled: false,
		errorLoggingEnabled: true,
		handleErrorEnabled: true,
		requiresAuthentication: true,
		context: new HttpContext(),
		withCredentials: false,
	};
}
